import React, { useEffect, useState, useCallback } from "react";
import { api, extractApiError } from "@/lib/api";
import { useT } from "@/lib/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PageLoadError } from "./NoAccess";
import { Key, Save, Trash2, Loader2, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";

const GROUP_META = {
  paypal: { name: "PayPal", color: "from-[#0070BA] to-[#003087]", docs: "https://developer.paypal.com/" },
  google: { name: "Google Calendar", color: "from-[#4285F4] to-[#1A73E8]", docs: "https://console.cloud.google.com/apis/credentials" },
  email: { name: "Email (Resend)", color: "from-[#000000] to-[#3F3F46]", docs: "https://resend.com/api-keys" },
};

/**
 * PlatformCredentials — SuperAdmin page pour gérer les clés API PayPal/Stripe
 * en DB (chiffrées) au lieu de .env. Priorité DB > env au runtime.
 * Route: /super-admin/platform-credentials
 */
export default function PlatformCredentials() {
  const t = useT("superadmin");
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [reveal, setReveal] = useState({});
  const [saving, setSaving] = useState(null);
  const [loadError, setLoadError] = useState(null);

  // A failed read is shown as one: an empty credential list would claim nothing is configured.
  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await api.get("/super-admin/platform-settings");
      setSettings(data || []);
    } catch (e) {
      setLoadError(e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (key) => {
    const value = drafts[key];
    if (value === undefined || value === "") return toast.error(t("sa.cred.emptyValue"));
    setSaving(key);
    try {
      await api.put(`/super-admin/platform-settings/${key}`, { value });
      setDrafts({ ...drafts, [key]: undefined });
      toast.success(t("sa.cred.saved", { key }));
      load();
    } catch (e) {
      toast.error(extractApiError(e, t("sa.cred.error"), t));
    } finally { setSaving(null); }
  };

  const remove = async (key) => {
    if (!window.confirm(t("sa.cred.confirmDelete", { key }))) return;
    try {
      await api.delete(`/super-admin/platform-settings/${key}`);
    } catch (e) { toast.error(extractApiError(e, t("sa.cred.error"), t)); return; }
    toast.success(t("sa.cred.deleted"));
    load();
  };

  const grouped = settings.reduce((acc, s) => {
    (acc[s.group] = acc[s.group] || []).push(s);
    return acc;
  }, {});

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-amber-600" /></div>;
  if (loadError) return <PageLoadError error={loadError} onRetry={load} testId="credentials-load-error" />;

  return (
    <div data-testid="platform-credentials" className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-sans text-3xl text-slate-800 flex items-center gap-3">
          <Key className="w-6 h-6 text-amber-600" /> {t("sa.cred.title")}
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          {t("sa.cred.subtitle")}
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            {t("sa.cred.securityNote")}
          </div>
        </div>
      </div>

      {Object.entries(grouped).map(([group, items]) => {
        const meta = GROUP_META[group] || { name: group, color: "from-gray-600 to-gray-800" };
        return (
          <div key={group} className="bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden">
            <div className={`h-2 bg-gradient-to-r ${meta.color}`} />
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-sans text-xl text-slate-800">{meta.name}</h3>
                {meta.docs && (
                  <a href={meta.docs} target="_blank" rel="noopener noreferrer" className="text-xs text-amber-600 hover:underline">
                    {t("sa.cred.documentation")}
                  </a>
                )}
              </div>
              <div className="space-y-3">
                {items.map((s) => {
                  const draftVal = drafts[s.key];
                  const isEditing = draftVal !== undefined;
                  return (
                    <div key={s.key} className="bg-white rounded-xl border border-slate-200 p-3" data-testid={`setting-${s.key}`}>
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <div>
                          <div className="text-sm font-medium text-slate-800">{s.label}</div>
                          <code className="text-[10px] text-slate-400">{s.key}</code>
                        </div>
                        <div className="flex items-center gap-2">
                          {s.is_set ? (
                            <span className="text-[10px] uppercase bg-emerald-100 text-emerald-600 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> {s.source}
                            </span>
                          ) : (
                            <span className="text-[10px] uppercase bg-rose-500/20 text-rose-600 px-2 py-0.5 rounded-full">{t("sa.cred.missing")}</span>
                          )}
                        </div>
                      </div>
                      {!isEditing && s.is_set && !s.is_secret && s.preview && (
                        <div className="text-xs text-slate-500 font-mono mb-2">{s.preview}</div>
                      )}
                      <div className="flex gap-2">
                        <Input
                          type={s.is_secret && !reveal[s.key] ? "password" : "text"}
                          value={draftVal ?? ""}
                          onChange={(e) => setDrafts({ ...drafts, [s.key]: e.target.value })}
                          placeholder={s.is_set ? t("sa.cred.replacePlaceholder") : t("sa.cred.pastePlaceholder")}
                          data-testid={`input-${s.key}`}
                          className="bg-slate-50 border-slate-200 text-slate-800 text-xs h-8"
                        />
                        {s.is_secret && isEditing && (
                          <button
                            onClick={() => setReveal({ ...reveal, [s.key]: !reveal[s.key] })}
                            className="p-1.5 text-slate-500 hover:text-slate-800"
                          >
                            {reveal[s.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => save(s.key)}
                          disabled={!isEditing || saving === s.key}
                          data-testid={`save-${s.key}`}
                          className="bg-sol-gold text-black hover:bg-sol-gold/90"
                        >
                          {saving === s.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        </Button>
                        {s.is_set && s.source === "db" && (
                          <button
                            onClick={() => remove(s.key)}
                            className="p-1.5 text-rose-400 hover:bg-rose-50 rounded"
                            data-testid={`del-${s.key}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
